import {
  computeVariationAmounts,
  calculateJobPayoutSplit,
  payoutStatusLabel,
  isPayoutSettled,
  type VariationAmounts,
  type JobPayoutSplitResult,
} from './financial';
import { extractGstFromInclusive, normalizeCommissionRate } from '../utils/currency';
import {
  getCommissionRateBpsFromTiers,
  DEFAULT_COMMISSION_TIERS,
} from '../utils/commission';
import { payoutGrossCents, type PayoutAmountRow } from './payoutAmount';

// =============================================================================
// Trade Payout Breakdown (Unified between Web and Mobile)
// =============================================================================

/** What the trade is paid, in dollars. It holds no client price and no commission rate. */
export interface PayoutBreakdown {
  /** The trade's payout, ex GST. */
  net_payout: number;
  /** GST on the payout (0 when the trade is not GST registered). */
  trade_gst: number;
  /** What the trade receives, inc its GST. */
  total_receive: number;
  trade_gst_registered: boolean;
  /** True when priced from the job's listed price rather than a locked payout. */
  is_estimate: boolean;
}

/** Converts a calculate_trade_payout row into a standardized breakdown. */
export function toPayoutBreakdown(
  row: {
    trade_net_cents?: number | null;
    trade_gst_cents?: number | null;
    trade_total_cents?: number | null;
    trade_gst_registered?: boolean | null;
    is_estimate?: boolean | null;
  } | null | undefined
): PayoutBreakdown | null {
  if (!row || row.trade_net_cents == null) return null;
  return {
    net_payout: row.trade_net_cents / 100,
    trade_gst: (row.trade_gst_cents ?? 0) / 100,
    total_receive: (row.trade_total_cents ?? row.trade_net_cents) / 100,
    trade_gst_registered: row.trade_gst_registered === true,
    is_estimate: row.is_estimate === true,
  };
}

// =============================================================================
// Job Financial Summary
// =============================================================================

export interface JobFinancialRowInput {
  client_total_cents?: number | null;
  gst_amount_cents?: number | null;
  estimated_value?: number | null;
  commission_rate_bps?: number | null;
  trade_net_cents?: number | null;
  trade_gst_amount_cents?: number | null;
  trade_total_cents?: number | null;
  trade_gst_registered?: boolean | null;
  // Container job support
  job_role?: string | null;
  subjob_count?: number | null;
  subjobs_client_total_cents?: number | null;
  subjobs_gst_amount_cents?: number | null;
  subjobs_estimated_value?: number | null;
}

export interface JobFinancialSummaryOptions {
  tradeGstRegistered?: boolean | null;
  defaultCommissionBps?: number;
}

export interface JobDisplayFinancials {
  clientSubtotalCents: number;
  clientGstCents: number;
  clientTotalCents: number;
  commissionCents: number;
  commissionBps: number;
  tradeNetCents: number;
  tradeGstCents: number;
  tradeTotalCents: number;
  // In dollars:
  clientSubtotal: number;
  clientGst: number;
  clientTotal: number;
  platformSubtotal: number;
  commission: number;
  tradeSubtotal: number;
  tradeNet: number;
  tradeGst: number;
  tradeTotal: number;
  clientHasReal: boolean;
  tradeGstRegistered: boolean;
}

/**
 * Derives canonical headline financial figures for displaying a job on web and mobile.
 * Adheres strictly to registration-aware trade GST rules (0 GST if unregistered).
 */
export function jobFinancialSummary(
  row: JobFinancialRowInput,
  options?: JobFinancialSummaryOptions
): JobDisplayFinancials {
  const isContainer = row.job_role === 'CONTAINER';
  const hasSubjobs = isContainer && (row.subjob_count ?? 0) > 0;

  const rawClientTotal = hasSubjobs
    ? row.subjobs_client_total_cents
    : row.client_total_cents;
  const rawGst = hasSubjobs
    ? row.subjobs_gst_amount_cents
    : row.gst_amount_cents;
  const rawEst = hasSubjobs
    ? (row.subjobs_estimated_value ?? row.estimated_value ?? 0)
    : (row.estimated_value ?? 0);

  const clientHasReal = rawClientTotal != null && Number.isFinite(rawClientTotal);
  let clientTotalCents: number;
  let clientGstCents: number;
  let clientSubtotalCents: number;

  if (clientHasReal) {
    clientTotalCents = Math.round(rawClientTotal!);
    if (rawGst != null && Number.isFinite(rawGst)) {
      clientGstCents = Math.round(rawGst);
    } else {
      clientGstCents = extractGstFromInclusive(clientTotalCents).gst;
    }
    clientSubtotalCents = clientTotalCents - clientGstCents;
  } else {
    const estDollars = Number.isFinite(rawEst) ? Math.max(0, rawEst) : 0;
    clientSubtotalCents = Math.round(estDollars * 100);
    clientGstCents = Math.round(clientSubtotalCents * 0.10);
    clientTotalCents = clientSubtotalCents + clientGstCents;
  }

  // Resolve Commission and Trade Net
  let commissionCents: number;
  let tradeNetCents: number;
  let commissionBps: number;

  if (row.trade_net_cents != null && Number.isFinite(row.trade_net_cents)) {
    tradeNetCents = Math.round(row.trade_net_cents);
    commissionCents = clientSubtotalCents - tradeNetCents;
    commissionBps =
      row.commission_rate_bps != null && Number.isFinite(row.commission_rate_bps)
        ? Math.round(row.commission_rate_bps)
        : clientSubtotalCents > 0
        ? Math.round((commissionCents / clientSubtotalCents) * 10000)
        : 0;
  } else {
    commissionBps =
      row.commission_rate_bps != null && Number.isFinite(row.commission_rate_bps)
        ? Math.round(row.commission_rate_bps)
        : options?.defaultCommissionBps ??
          getCommissionRateBpsFromTiers(clientSubtotalCents, DEFAULT_COMMISSION_TIERS);
    commissionCents = Math.round((clientSubtotalCents * commissionBps) / 10000);
    tradeNetCents = clientSubtotalCents - commissionCents;
  }

  // Resolve Trade GST: stored snapshot wins; unknown registration charges GST like server
  const rawRegistered = options?.tradeGstRegistered ?? row.trade_gst_registered;
  const isRegistered = rawRegistered !== false;

  let tradeGstCents: number;
  if (
    row.trade_gst_amount_cents != null &&
    Number.isFinite(row.trade_gst_amount_cents)
  ) {
    tradeGstCents = Math.round(row.trade_gst_amount_cents);
  } else if (rawRegistered === false) {
    tradeGstCents = 0;
  } else {
    tradeGstCents = Math.round(tradeNetCents * 0.10);
  }

  const tradeTotalCents =
    row.trade_total_cents != null && Number.isFinite(row.trade_total_cents)
      ? Math.round(row.trade_total_cents)
      : tradeNetCents + tradeGstCents;

  return {
    clientSubtotalCents,
    clientGstCents,
    clientTotalCents,
    commissionCents,
    commissionBps,
    tradeNetCents,
    tradeGstCents,
    tradeTotalCents,
    clientSubtotal: clientSubtotalCents / 100,
    clientGst: clientGstCents / 100,
    clientTotal: clientTotalCents / 100,
    platformSubtotal: commissionCents / 100,
    commission: commissionCents / 100,
    tradeSubtotal: tradeNetCents / 100,
    tradeNet: tradeNetCents / 100,
    tradeGst: tradeGstCents / 100,
    tradeTotal: tradeTotalCents / 100,
    clientHasReal,
    tradeGstRegistered: isRegistered,
  };
}

// =============================================================================
// Quote Summary
// =============================================================================

export interface QuoteDisplayInput {
  total_amount?: number | null;
  subtotal?: number | null;
  gst_amount?: number | null;
  commission_rate?: number | null;
  commission_rate_bps?: number | null;
  trade_total_cents?: number | null;
  trade_net_cents?: number | null;
  trade_gst_cents?: number | null;
  trade_gst_registered?: boolean | null;
  trade_company?: { gst_registered?: boolean | null } | null;
}

export interface QuoteSummaryOptions {
  tradeGstRegistered?: boolean | null;
  defaultCommissionBps?: number;
}

export interface QuoteDisplayFinancials {
  clientTotalCents: number;
  clientSubtotalCents: number;
  clientGstCents: number;
  commissionCents: number;
  commissionBps: number;
  commissionRate: number;
  tradeNetCents: number;
  tradeGstCents: number;
  tradeTotalCents: number;
  clientTotal: number;
  clientSubtotal: number;
  clientGst: number;
  commission: number;
  platformMargin: number;
  tradeNet: number;
  tradePrice: number;
  tradeGst: number;
  tradeTotal: number;
  tradeTotalReceive: number;
  tradeGstRegistered: boolean;
}

/**
 * Computes display figures for a quote row or preview.
 * Prefers `commission_rate_bps`, falling back to `commission_rate` (percent) and master default 20%.
 * Stored snapshot figures win; unknown registration charges GST like the server Oracle.
 */
export function quoteSummary(
  quote: QuoteDisplayInput,
  options?: QuoteSummaryOptions
): QuoteDisplayFinancials {
  const clientTotalCents = Math.round((quote.total_amount ?? 0) * 100);
  const clientSubtotalCents =
    quote.subtotal != null
      ? Math.round(quote.subtotal * 100)
      : extractGstFromInclusive(clientTotalCents).exGst;
  const clientGstCents =
    quote.gst_amount != null
      ? Math.round(quote.gst_amount * 100)
      : clientTotalCents - clientSubtotalCents;

  let commissionBps: number;
  if (
    quote.commission_rate_bps != null &&
    Number.isFinite(quote.commission_rate_bps)
  ) {
    commissionBps = Math.round(quote.commission_rate_bps);
  } else if (
    quote.commission_rate != null &&
    Number.isFinite(quote.commission_rate)
  ) {
    commissionBps = Math.round(
      normalizeCommissionRate(quote.commission_rate, { unit: 'percent' }) * 100
    );
  } else {
    commissionBps = options?.defaultCommissionBps ?? 2000;
  }

  let commissionCents: number;
  let tradeNetCents: number;
  let commissionRate: number;

  if (quote.trade_net_cents != null && Number.isFinite(quote.trade_net_cents)) {
    tradeNetCents = Math.round(quote.trade_net_cents);
    commissionCents = clientSubtotalCents - tradeNetCents;
    commissionRate = clientSubtotalCents > 0
      ? Math.round(((commissionCents / clientSubtotalCents) * 100) * 10) / 10
      : 0;
    commissionBps = Math.round(commissionRate * 100);
  } else {
    commissionCents = Math.round((clientSubtotalCents * commissionBps) / 10000);
    tradeNetCents = clientSubtotalCents - commissionCents;
    commissionRate = commissionBps / 100;
  }

  const rawRegistered =
    options?.tradeGstRegistered ??
    quote.trade_gst_registered ??
    quote.trade_company?.gst_registered;

  let tradeGstCents: number;
  if (
    quote.trade_gst_cents != null &&
    Number.isFinite(quote.trade_gst_cents)
  ) {
    tradeGstCents = Math.round(quote.trade_gst_cents);
  } else if (rawRegistered === false) {
    tradeGstCents = 0;
  } else {
    tradeGstCents = Math.round(tradeNetCents * 0.10);
  }

  const tradeTotalCents =
    quote.trade_total_cents != null && Number.isFinite(quote.trade_total_cents)
      ? Math.round(quote.trade_total_cents)
      : tradeNetCents + tradeGstCents;

  const isRegistered =
    quote.trade_gst_cents != null
      ? tradeGstCents > 0
      : rawRegistered !== false;

  const clientTotal = clientTotalCents / 100;
  const clientSubtotal = clientSubtotalCents / 100;
  const clientGst = clientGstCents / 100;
  const commission = commissionCents / 100;
  const tradeNet = tradeNetCents / 100;
  const tradeGst = tradeGstCents / 100;
  const tradeTotal = tradeTotalCents / 100;

  return {
    clientTotalCents,
    clientSubtotalCents,
    clientGstCents,
    commissionCents,
    commissionBps,
    commissionRate,
    tradeNetCents,
    tradeGstCents,
    tradeTotalCents,
    clientTotal,
    clientSubtotal,
    clientGst,
    commission,
    platformMargin: commission,
    tradeNet,
    tradePrice: tradeNet,
    tradeGst,
    tradeTotal,
    tradeTotalReceive: tradeTotal,
    tradeGstRegistered: isRegistered,
  };
}

// =============================================================================
// Variation Summary
// =============================================================================

export interface VariationDisplayInput {
  trade_amount_cents?: number | null;
  commission_amount_cents?: number | null;
  markup_amount_cents?: number | null;
  customer_total_cents?: number | null;
  /** Trade-entered dollar amount when previewing a new variation */
  amount?: number | null;
  commission_rate_bps?: number | null;
  markup_percentage?: number | null;
}

export interface VariationSummaryOptions {
  tradeGstRegistered?: boolean | null;
}

export interface VariationDisplayFinancials {
  tradeAmountCents: number;
  commissionAmountCents: number;
  markupAmountCents: number;
  subtotalCents: number;
  gstAmountCents: number;
  cardChargeCents: number;
  markupIncGstCents: number;
  customerTotalCents: number;
  tradeGstCents: number;
  totalTransferCents: number;
  // Dollars:
  tradeAmount: number;
  commissionAmount: number;
  markupAmount: number;
  cardCharge: number;
  markupIncGst: number;
  customerTotal: number;
  tradeGst: number;
  totalTransfer: number;
  tradeGstRegistered: boolean;
}

/**
 * Computes display figures for a variation row or preview.
 * Card charge = (trade + commission) * 1.1 inc GST.
 * Trade GST = 0 if unregistered.
 */
export function variationSummary(
  variation: VariationDisplayInput,
  options?: VariationSummaryOptions
): VariationDisplayFinancials {
  let tradeAmountCents = 0;
  let commissionAmountCents = 0;
  let markupAmountCents = 0;
  let subtotalCents = 0;
  let gstAmountCents = 0;
  let customerTotalCents = 0;

  if (variation.amount != null && Number.isFinite(variation.amount)) {
    const amounts: VariationAmounts = computeVariationAmounts({
      amount: variation.amount,
      commissionRateBps: variation.commission_rate_bps ?? 0,
      markupPercentage: variation.markup_percentage ?? 0,
    });
    tradeAmountCents = amounts.tradeAmountCents;
    commissionAmountCents = amounts.commissionAmountCents;
    markupAmountCents = amounts.markupAmountCents;
    subtotalCents = amounts.subtotalCents;
    gstAmountCents = amounts.gstAmountCents;
    customerTotalCents = amounts.customerTotalCents;
  } else {
    tradeAmountCents = Math.round(variation.trade_amount_cents ?? 0);
    commissionAmountCents = Math.round(variation.commission_amount_cents ?? 0);
    markupAmountCents = Math.round(variation.markup_amount_cents ?? 0);
    subtotalCents = tradeAmountCents + commissionAmountCents + markupAmountCents;
    gstAmountCents = Math.round(subtotalCents * 0.10);
    customerTotalCents =
      variation.customer_total_cents != null &&
      Number.isFinite(variation.customer_total_cents)
        ? Math.round(variation.customer_total_cents)
        : subtotalCents + gstAmountCents;
  }

  const cardChargeCents = Math.round(
    (tradeAmountCents + commissionAmountCents) * 1.10
  );
  const markupIncGstCents = Math.round(markupAmountCents * 1.10);

  const isRegistered = options?.tradeGstRegistered ?? false;
  const tradeGstCents = isRegistered ? Math.round(tradeAmountCents * 0.10) : 0;
  const totalTransferCents = tradeAmountCents + tradeGstCents;

  return {
    tradeAmountCents,
    commissionAmountCents,
    markupAmountCents,
    subtotalCents,
    gstAmountCents,
    cardChargeCents,
    markupIncGstCents,
    customerTotalCents,
    tradeGstCents,
    totalTransferCents,
    tradeAmount: tradeAmountCents / 100,
    commissionAmount: commissionAmountCents / 100,
    markupAmount: markupAmountCents / 100,
    cardCharge: cardChargeCents / 100,
    markupIncGst: markupIncGstCents / 100,
    customerTotal: customerTotalCents / 100,
    tradeGst: tradeGstCents / 100,
    totalTransfer: totalTransferCents / 100,
    tradeGstRegistered: isRegistered,
  };
}

// =============================================================================
// Payout Summary
// =============================================================================

export interface PayoutSummaryInput {
  trade_net_cents?: number | null;
  trade_gst_amount_cents?: number | null;
  trade_total_cents?: number | null;
  trade_gst_registered?: boolean | null;
  completed_at?: string | null;
  payout_status?: string | null;
  initial_payout_percentage?: number | null;
  trade_settled_amount_cents?: number | null;
  settled_trade_amount_cents?: number | null;
}

export interface PayoutDisplaySummary {
  tradeNetCents: number | null;
  tradeGstCents: number | null;
  tradeTotalCents: number | null;
  tradeGstRegistered: boolean;
  initialPercentage: number;
  deferredPercentage: number;
  initialNetCents: number | null;
  initialGstCents: number | null;
  initialTotalCents: number | null;
  holdbackNetCents: number | null;
  holdbackGstCents: number | null;
  holdbackTotalCents: number | null;
  earnedCents: number;
  owedCents: number;
  completedAmountCents: number;
  completedGstCents: number;
  pendingAmountCents: number;
  pendingGstCents: number;
  statusLabel: string;
  isSettled: boolean;
  tradeNet: number | null;
  tradeGst: number | null;
  tradeTotal: number | null;
  initialTotal: number | null;
  holdbackTotal: number | null;
  earned: number;
  owed: number;
}

/**
 * Computes split amounts and status summaries for trade payouts.
 * Honors the Aug 3, 2026 cutover (80/20 legacy vs 60/40), holdback refunds, and settlements.
 * Stored figures win; null net stays null.
 */
export function payoutSummary(
  job: PayoutSummaryInput,
  payoutRows?: PayoutAmountRow[] | null,
  options?: { tradeGstRegistered?: boolean | null }
): PayoutDisplaySummary {
  const rawRegistered = options?.tradeGstRegistered ?? job.trade_gst_registered;
  const isRegistered = rawRegistered !== false;

  const statusLabel = payoutStatusLabel(job.payout_status);
  const isSettled = isPayoutSettled(job.payout_status);

  if (job.trade_net_cents == null || !Number.isFinite(job.trade_net_cents)) {
    let earnedCents = 0;
    if (Array.isArray(payoutRows) && payoutRows.length > 0) {
      for (const r of payoutRows) {
        earnedCents += payoutGrossCents(r);
      }
    }

    const storedGstCents =
      job.trade_gst_amount_cents != null && Number.isFinite(job.trade_gst_amount_cents)
        ? Math.round(job.trade_gst_amount_cents)
        : null;
    const storedTotalCents =
      job.trade_total_cents != null && Number.isFinite(job.trade_total_cents)
        ? Math.round(job.trade_total_cents)
        : null;

    return {
      tradeNetCents: null,
      tradeGstCents: storedGstCents,
      tradeTotalCents: storedTotalCents,
      tradeGstRegistered: isRegistered,
      initialPercentage: 0,
      deferredPercentage: 0,
      initialNetCents: null,
      initialGstCents: null,
      initialTotalCents: null,
      holdbackNetCents: null,
      holdbackGstCents: null,
      holdbackTotalCents: null,
      earnedCents,
      owedCents: 0,
      completedAmountCents: 0,
      completedGstCents: 0,
      pendingAmountCents: 0,
      pendingGstCents: 0,
      statusLabel,
      isSettled,
      tradeNet: null,
      tradeGst: storedGstCents != null ? storedGstCents / 100 : null,
      tradeTotal: storedTotalCents != null ? storedTotalCents / 100 : null,
      initialTotal: null,
      holdbackTotal: null,
      earned: earnedCents / 100,
      owed: 0,
    };
  }

  const tradeNetCents = Math.round(job.trade_net_cents);
  let tradeGstCents: number;
  if (
    job.trade_gst_amount_cents != null &&
    Number.isFinite(job.trade_gst_amount_cents)
  ) {
    tradeGstCents = Math.round(job.trade_gst_amount_cents);
  } else if (rawRegistered === false) {
    tradeGstCents = 0;
  } else {
    tradeGstCents = Math.round(tradeNetCents * 0.10);
  }

  const tradeTotalCents =
    job.trade_total_cents != null && Number.isFinite(job.trade_total_cents)
      ? Math.round(job.trade_total_cents)
      : tradeNetCents + tradeGstCents;

  const split: JobPayoutSplitResult = calculateJobPayoutSplit({
    tradeNetCents,
    tradeGstCents,
    completedAt: job.completed_at,
    payoutStatus: job.payout_status,
    initialPayoutPercentage: job.initial_payout_percentage,
    settledTradeAmountCents:
      job.settled_trade_amount_cents ?? job.trade_settled_amount_cents,
  });

  let earnedCents = split.completedAmountCents + split.completedGstCents;
  let owedCents = split.pendingAmountCents + split.pendingGstCents;

  if (Array.isArray(payoutRows) && payoutRows.length > 0) {
    let releasedGross = 0;
    for (const r of payoutRows) {
      releasedGross += payoutGrossCents(r);
    }
    // If rows were provided, use actual released gross if job is partially or fully released
    if (job.payout_status === 'FULLY_RELEASED' || job.payout_status === 'PARTIALLY_RELEASED') {
      earnedCents = releasedGross;
      owedCents = Math.max(0, tradeTotalCents - earnedCents);
    }
  }

  return {
    tradeNetCents,
    tradeGstCents,
    tradeTotalCents,
    tradeGstRegistered: isRegistered,
    initialPercentage: split.initialPercentage,
    deferredPercentage: split.deferredPercentage,
    initialNetCents: split.initialNetCents,
    initialGstCents: split.initialGstCents,
    initialTotalCents: split.initialTotalCents,
    holdbackNetCents: split.holdbackNetCents,
    holdbackGstCents: split.holdbackGstCents,
    holdbackTotalCents: split.holdbackTotalCents,
    earnedCents,
    owedCents,
    completedAmountCents: split.completedAmountCents,
    completedGstCents: split.completedGstCents,
    pendingAmountCents: split.pendingAmountCents,
    pendingGstCents: split.pendingGstCents,
    statusLabel,
    isSettled,
    tradeNet: tradeNetCents / 100,
    tradeGst: tradeGstCents / 100,
    tradeTotal: tradeTotalCents / 100,
    initialTotal: split.initialTotalCents / 100,
    holdbackTotal: split.holdbackTotalCents / 100,
    earned: earnedCents / 100,
    owed: owedCents / 100,
  };
}
